import { frequentFlyerNumberTravellerFormFields, FrequentFlyerField } from 'constants/traveller'
import { FlightFieldSchema } from 'api/traveller'

/**
 * Extracts valid airline codes from a single frequentFlyerAirline field's enum.
 * The enum is not typed in the contract but is present at runtime as an array
 * of objects with value, label and image properties (see FlightFieldSchema).
 * Excludes the 'no_selection' placeholder.
 */
function getFieldValidAirlineCodes(fieldSchema: FlightFieldSchema | undefined): Set<string> {
  const airlineEnums = fieldSchema?.enum
  if (!airlineEnums || !Array.isArray(airlineEnums)) return new Set()

  return new Set(airlineEnums.filter(entry => entry.value && entry.value !== 'no_selection').map(entry => entry.value))
}

/**
 * Returns the available frequent flyer schema slots for a traveller.
 * Each slot maps to a frequentFlyerAirline/frequentFlyerNumber field pair
 * that exists in the traveller schema, along with the set of airline codes
 * that slot's dropdown supports. Only includes slots present in the schema —
 * e.g. if the schema only has frequentFlyerAirline2, only that slot is returned.
 */
export type FrequentFlyerSlot = {
  airlineField: FrequentFlyerField
  numberField: FrequentFlyerField
  validAirlines: Set<string>
}

export function getSchemaFrequentFlyerFields(travellerSchema?: App.Checkout.TravellerFormSchema): Array<FrequentFlyerSlot> {
  if (!travellerSchema) return []

  return frequentFlyerNumberTravellerFormFields
    .filter(([airlineField]) => travellerSchema.properties[airlineField])
    .map(([airlineField, numberField]) => ({
      airlineField,
      numberField,
      validAirlines: getFieldValidAirlineCodes(travellerSchema.properties[airlineField] as FlightFieldSchema | undefined),
    }))
}

/**
 * Collects frequent flyer numbers from loyalty programs into a map keyed by airline code.
 * Returns Record<airlineCode, frequentFlyerNumber>, e.g. { 'QF': '11111', 'VA': '22222' }.
 * Airline codes are unique across slots — the traveller form dropdown deduplicates
 * already-saved airlines, so each airline appears at most once.
 */
export function getLoyaltyProgramsByAirline(
  loyaltyPrograms: App.BusinessTraveller.TravellerFormLoyaltyPrograms,
): Record<string, string> {
  return frequentFlyerNumberTravellerFormFields.reduce<Record<string, string>>((acc, [airlineField, numberField]) => {
    const airline = loyaltyPrograms[airlineField]
    const number = loyaltyPrograms[numberField]
    if (airline && number) {
      acc[airline] = number
    }
    return acc
  }, {})
}

/**
 * Validates and maps trip request loyalty programs onto the current traveller schema.
 * The trip request may have been created with different flights than the ones being
 * purchased, so the saved airline/FFN pairs may reference airlines no longer in the schema.
 *
 * For each schema slot, finds the first unused trip request FFN pair whose airline
 * is in that slot's valid enum. Sets both the airline and number fields in the result.
 */
export function sanitiseTripRequestLoyaltyPrograms(
  loyaltyPrograms: App.BusinessTraveller.TravellerFormLoyaltyPrograms,
  travellerSchema?: App.Checkout.TravellerFormSchema,
): App.BusinessTraveller.TravellerFormLoyaltyPrograms {
  const frequentFlyerFields = getSchemaFrequentFlyerFields(travellerSchema)
  if (frequentFlyerFields.length === 0) return {}

  const loyaltyProgramsByAirline = getLoyaltyProgramsByAirline(loyaltyPrograms)
  const airlineKeys = Object.keys(loyaltyProgramsByAirline)
  if (airlineKeys.length === 0) return {}

  // For each schema slot, find a matching airline from the trip request
  const result: App.BusinessTraveller.TravellerFormLoyaltyPrograms = {}
  const usedAirlines = new Set<string>()

  for (const frequentFlyerField of frequentFlyerFields) {
    const matchedAirline = airlineKeys.find(airline =>
      !usedAirlines.has(airline) && frequentFlyerField.validAirlines.has(airline),
    )
    if (!matchedAirline) continue

    usedAirlines.add(matchedAirline)
    result[frequentFlyerField.airlineField] = matchedAirline
    result[frequentFlyerField.numberField] = loyaltyProgramsByAirline[matchedAirline]
  }

  return result
}

/**
 * Extracts valid frequent flyer pairs (airline + number) from a traveller form's data.
 * Only includes pairs where both airline and number are present.
 */
export function getValidFrequentFlyerPairs(data: App.Checkout.TravellerFormData): Array<[string, string]> {
  return frequentFlyerNumberTravellerFormFields.flatMap(([airlineField, numberField]) => {
    const airline = data[airlineField]
    const number = data[numberField]
    return airline && number ? [[airline, number]] : []
  })
}

/**
 * Generates a hash string from traveller form data (names + loyalty accounts)
 * used as part of the seat availability request key to detect changes and trigger refetch.
 * Only includes travellers that have at least one loyalty program configured.
 * Returns undefined if no travellers have loyalty data.
 */
export function generateTravellerDataHash(
  travellerForms: Array<App.Checkout.TravellerForm>,
): string | undefined {
  if (travellerForms.length === 0) return undefined

  const hashes = travellerForms.flatMap(travellerForm => {
    const pairs = getValidFrequentFlyerPairs(travellerForm.data)
    if (pairs.length === 0) return []

    return [[
      travellerForm.id,
      travellerForm.data.firstName,
      travellerForm.data.lastName,
      ...pairs.flat(),
    ].filter(Boolean).join('&')]
  })

  return hashes.length > 0 ? hashes.join('|') : undefined
}
